import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Soodam free-shipping progress live updater (the baduk line).
 *
 * Drives every `[data-shipping-progress]` node — cart drawer, /cart page,
 * and the add-to-cart confirmation modal — from one place, so the surfaces
 * stay in lockstep.
 *
 * Free shipping is a per-zone shipping rate (flat below the market's
 * threshold, $0 at/above it), NOT a discount — so there is no cart-level
 * discount application to read. Qualification is `subtotal >= threshold`,
 * in the buyer's presentment currency. Subtotal is `items_subtotal_price`
 * (the merchandise subtotal the shipping rate's TOTAL_PRICE condition
 * evaluates). The threshold and copy templates are read from `data-*`
 * attributes set server-side by `snippets/soodam-shipping-progress.liquid`.
 *
 * Refreshed on `cart:update`, which fires on every add/remove and carries
 * the new cart as its resource. The upsell modal is markup rendered at page
 * load, so it depends on this repaint too: the add-to-cart flow dispatches
 * `cart:update` (CartAddEvent) and the modal only opens after awaiting, so
 * this synchronous listener always paints before the dialog is shown.
 *
 * Loaded once per page from `snippets/scripts.liquid` so the listener
 * survives section morphs (morphed sections do not execute inline scripts).
 */

const SELECTOR = '[data-shipping-progress]';

/**
 * @param {HTMLElement} node
 * @returns {{
 *   thresholdCents: number,
 *   currency: string,
 *   moneyFormat: string,
 *   hideEmpty: boolean,
 *   labelProgress: string,
 *   labelQualified: string,
 *   labelEmpty: string,
 * } | null}
 */
function readConfig(node) {
  const thresholdCents = Number(node.dataset.thresholdCents);
  if (!thresholdCents) return null;

  return {
    thresholdCents,
    currency: node.dataset.currency || '',
    moneyFormat: node.dataset.moneyFormat || '',
    hideEmpty: node.dataset.hideEmpty === 'true',
    labelProgress: node.dataset.labelProgress || 'Add [amount] more for free shipping.',
    labelQualified: node.dataset.labelQualified || 'Your order ships free.',
    labelEmpty: node.dataset.labelEmpty || '',
  };
}

const prefersReducedMotion =
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Play the placed-stone unlock once: re-adding the class after a forced
 * reflow restarts the ripple/pulse animations, so a cart that dips below the
 * threshold and clears it again celebrates each time. Suppressed under
 * reduced motion (the static celadon state still reads as cleared).
 * @param {HTMLElement} node
 */
function celebrate(node) {
  if (prefersReducedMotion) return;
  node.classList.remove('soodam-ship--placed');
  // Force reflow so the animation restarts even on a repeat unlock.
  void node.offsetWidth;
  node.classList.add('soodam-ship--placed');
}

/**
 * @param {HTMLElement} node
 * @param {'empty' | 'progress' | 'qualified'} state
 * @param {number} ratio - Clamped 0..1.
 * @param {string} label
 * @param {number} remainingCents - Cents still needed, 0 once qualified. Mirrored
 *   to `data-remaining-cents` so analytics (upsell-modal.js) can read the meter's
 *   state at capture time. Presentment currency — markets differ, no conversion.
 */
function paint(node, state, ratio, label, remainingCents) {
  const previousState = node.dataset.state;
  node.dataset.state = state;
  node.dataset.remainingCents = String(Math.max(0, Math.round(remainingCents)));
  node.style.setProperty('--soodam-ship-progress', String(ratio));

  const labelEl = node.querySelector('[data-shipping-label]');
  if (labelEl && label != null) labelEl.textContent = label;

  if (state === 'empty' && node.dataset.hideEmpty === 'true') {
    node.setAttribute('hidden', '');
  } else {
    node.removeAttribute('hidden');
  }

  // Celebrate only on the transition INTO qualified, not when a page loads
  // already-qualified (previousState would already be 'qualified').
  if (state === 'qualified' && previousState && previousState !== 'qualified') {
    celebrate(node);
  }
}

/**
 * @param {number} subtotalCents - items_subtotal_price (minor units).
 * @param {number} itemCount
 */
function render(subtotalCents, itemCount) {
  for (const node of document.querySelectorAll(SELECTOR)) {
    if (!(node instanceof HTMLElement)) continue;
    const config = readConfig(node);
    if (!config) continue;

    if (itemCount <= 0) {
      paint(node, 'empty', 0, config.labelEmpty, config.thresholdCents);
      continue;
    }

    const remainingCents = config.thresholdCents - subtotalCents;
    const ratio = Math.max(0, Math.min(1, subtotalCents / config.thresholdCents));

    if (remainingCents <= 0) {
      paint(node, 'qualified', 1, config.labelQualified, 0);
      continue;
    }

    const amount = formatMoney(remainingCents, config.moneyFormat, config.currency);
    paint(node, 'progress', ratio, config.labelProgress.replace('[amount]', amount), remainingCents);
  }
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  const resource = /** @type {any} */ (event).detail?.resource;
  // A failed/absent cart payload (e.g. the dispatcher's own /cart.js fetch
  // errored) would otherwise leave every meter — including the one inside the
  // upsell modal about to open — showing the pre-add subtotal.
  if (!resource || typeof resource.item_count !== 'number') {
    renderFromCartJson();
    return;
  }

  const subtotal =
    typeof resource.items_subtotal_price === 'number'
      ? resource.items_subtotal_price
      : typeof resource.total_price === 'number'
        ? resource.total_price
        : 0;
  const itemCount = typeof resource.item_count === 'number' ? resource.item_count : 0;

  render(subtotal, itemCount);
}

/**
 * Fallback when a `cart:update` arrives without a usable cart payload: read
 * the live cart and paint from that.
 */
function renderFromCartJson() {
  if (!document.querySelector(SELECTOR)) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (!cart) return;
      const subtotal =
        typeof cart.items_subtotal_price === 'number'
          ? cart.items_subtotal_price
          : cart.total_price || 0;
      const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
      render(subtotal, itemCount);
    })
    .catch(() => {
      /* Server render already painted a sane state; leave it be. */
    });
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
